import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { withZod } from '@remix-validated-form/with-zod'
import moment from 'moment'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { graphql } from 'app/generated/gql'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { BudgetType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'

const DELETE_BUDGET_MUTATION = graphql(`
  mutation DeleteBudget($id: ID!) {
    deleteBudget(id: $id) {
      id
    }
  }
`)

const UPDATE_BUDGET_MUTATION = graphql(`
  mutation UpdateBudget($id: ID!, $input: UpdateBudgetInput!) {
    updateBudget(id: $id, input: $input) {
      id
    }
  }
`)

const CREATE_BUDGET_MUTATION = graphql(`
  mutation CreateBudget($input: CreateBudgetInput!) {
    createBudget(input: $input) {
      id
    }
  }
`)

const schema = z.object({
  id: z.string().optional(),
  orgId: z.string().optional(),
  workspaceId: z.string().optional(),
  locationId: z.string(),
  yearMonth: z.string().min(1),
  amount: zfd.numeric(z.number().min(0)),
  targetSpend: zfd.numeric(z.number().min(0).optional()),
  leadGoal: zfd.numeric(z.number().min(0).optional()),
  mqlGoal: zfd.numeric(z.number().min(0).optional()),
  patientGoal: zfd.numeric(z.number().min(0).optional()),
  channel: z.string(),
})

export const budgetValidator = withZod(schema)

type BudgetEditData = {
  locationId: string
  workspaceId: string
  orgId: string
  budget: BudgetType | undefined
  defaultYearMonth: string
  defaultChannel: string
}

export type UseBudgetEditReturn = {
  isEdit: boolean
  showSpend: boolean
  defaultValues: Record<string, unknown>
  hiddenInputs: { name: string; value: string | undefined }[]
  onDelete: () => void
  onBack: () => void
  onSubmit: (values: z.infer<typeof schema>) => Promise<void>
  validator: typeof budgetValidator
}

export function useBudgetEdit(): UseBudgetEditReturn {
  const { locationId = ``, budgetSlug = `new` } = useParams()
  const navigate = useNavigate()
  const checkPermission = useCheckPermission()
  const [data, setData] = useState<BudgetEditData>({
    locationId,
    workspaceId: ``,
    orgId: ``,
    budget: undefined,
    defaultYearMonth: moment.utc().format(`YYYY-MM`),
    defaultChannel: `Digital Marketing`,
  })

  useEffect(() => {
    if (budgetSlug === 'new') return
    gqlFetchList<BudgetType>(
      'budgets',
      { search: 'locationId:' + locationId },
      'id locationId yearMonth channel amount targetSpend leadGoal mqlGoal patientGoal',
    )
      .then((result) => {
        const match = result.hits.find((b: any) => b.id === budgetSlug || `${b.yearMonth}-${b.channel}` === budgetSlug)
        setData({
          locationId,
          workspaceId: ``,
          orgId: ``,
          budget: match,
          defaultYearMonth: moment.utc().format(`YYYY-MM`),
          defaultChannel: `Digital Marketing`,
        })
      })
      .catch(() => {})
  }, [locationId, budgetSlug])

  const showSpend = checkPermission([`org:marketing:see-spend`, `marketing:spend`])

  const budget = data.budget as BudgetType | undefined
  const isEdit = !!budget

  const defaultValues = {
    ...budget,
    yearMonth: budget ? moment.utc(budget?.yearMonth).format(`YYYY-MM`) : data.defaultYearMonth,
    channel: budget ? budget.channel : data.defaultChannel,
  }

  const hiddenInputs = [
    { name: `id`, value: budget ? budget.id : undefined },
    { name: `locationId`, value: data.locationId! },
    { name: `workspaceId`, value: data.workspaceId },
    { name: `orgId`, value: data.orgId },
  ]

  const onDelete = () => {
    if (!budget?.id) return
    gqlMutation(DELETE_BUDGET_MUTATION, { id: budget.id })
      .then(() => navigate(-1))
      .catch(() => {})
  }

  // The API derives org/workspace from the location, so the form only sends
  // locationId + month/channel + the numeric goals.
  const onSubmit = async (values: z.infer<typeof schema>) => {
    const input = {
      amount: values.amount,
      targetSpend: values.targetSpend,
      leadGoal: values.leadGoal,
      mqlGoal: values.mqlGoal,
      patientGoal: values.patientGoal,
    }
    try {
      if (budget?.id) {
        await gqlMutation(UPDATE_BUDGET_MUTATION, { id: budget.id, input })
      } else {
        await gqlMutation(CREATE_BUDGET_MUTATION, {
          input: { ...input, locationId: values.locationId, yearMonth: values.yearMonth, channel: values.channel },
        })
      }
      notifySuccess(`Budget saved`)
      navigate(-1)
    } catch (err) {
      logError(`Failed to save budget`, err)
      notifyError(`Failed to save budget`)
    }
  }

  const onBack = () => navigate(-1)

  return { isEdit, showSpend, defaultValues, hiddenInputs, onDelete, onBack, onSubmit, validator: budgetValidator }
}
